import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { BsModalRef, BsModalService, ModalOptions } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';

import { LoadingStatus } from '../../../../../../../../core/domain/events/state_provider';
import { Dietitian } from '../../../../../../../../data/dietitian/domain/dietitian';
import { PatientGroup } from '../../../../../../../../data/dietitian/domain/patient_group/patient_group';
import { Patient } from '../../../../../../../../data/patient/domain/patient';
import { ConfirmationModalComponent } from '../../../../../../../components/confirmation-modal/confirmation-modal.component';
import { AddPatientToGroupModalFacade } from './add-patient-to-group-modal.facade';

export interface AddPatientToGroupModalResponse {
  groupId: string;
  target: Patient[];
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'msd-add-patient-to-group-modal',
  templateUrl: './add-patient-to-group-modal.component.html',
  styleUrls: ['./add-patient-to-group-modal.component.scss'],
  providers: [AddPatientToGroupModalFacade],
})
export class AddPatientToGroupModalComponent implements OnInit, OnDestroy {
  @Input() target: Patient[] | Patient | PatientGroup;
  @Output() validate = new EventEmitter<AddPatientToGroupModalResponse>();
  @Output() cancel = new EventEmitter<void>();
  public isLoading = false;
  public isAdding = false;
  public error?: string;
  public selectedGroupId: string;
  public groups: PatientGroup[];
  private dietitianStateSub: Subscription;
  private dietitian?: Dietitian;

  constructor(
    private bsModalRef: BsModalRef,
    private facade: AddPatientToGroupModalFacade,
    private modalService: BsModalService,
    private router: Router,
  ) {
    // nothing
  }

  ngOnInit(): void {
    this.dietitianStateSub = this.facade.dietitianState$.subscribe((state) => {
      this.dietitian = state.entity;

      if (state.loading == LoadingStatus.COMPLETE) {
        this.loadPatientGroups().then();
      }
    });
  }

  ngOnDestroy(): void {
    this.dietitianStateSub?.unsubscribe();
  }

  createGroup() {
    this.hideModal();
    this.router
      .navigate(['/dashboard', 'groups'], {
        queryParams: {
          create: true,
        },
      })
      .then();
  }

  async loadPatientGroups() {
    this.isLoading = true;
    this.error = undefined;
    try {
      if (this.dietitian != null) {
        this.groups = await this.facade.getDietitianPatientGroups(
          this.dietitian?.id.toString(),
        );
      } else {
        this.groups = [];
      }
    } catch (error: unknown) {
      if (error instanceof Error) {
        this.error = error.message;
      }
    }
    this.isLoading = false;
  }

  alreadyGrouped(id: string) {
    if (Array.isArray(this.target)) {
      return false;
    } else {
      if (this.target instanceof Patient) {
        return this.target.groupId && this.target.groupId.indexOf(id) >= 0;
      } else {
        return false;
      }
    }
  }

  selectGroup(id: string) {
    if (Array.isArray(this.target)) {
      this.selectedGroupId = id;
    } else {
      if (this.target instanceof Patient) {
        if (!this.target.groupId || this.target.groupId.indexOf(id) < 0) {
          this.selectedGroupId = id;
        }
      } else {
        this.selectedGroupId = id;
      }
    }
  }

  hideModal() {
    this.cancel.emit();
    this.bsModalRef.hide();
  }

  async addPatientToGroup() {
    if (!this.selectedGroupId || !this.dietitian) {
      return;
    }
    const initialState: ModalOptions = {
      class: 'modal-md modal-dialog-centered',
      initialState: {
        title: `Profil & Trackers...`,
        subtitle: `Souhaitez-vous appliquer le profil de tracking du groupe <strong>${
          this.groups.filter((g) => g.id.toString() === this.selectedGroupId)[0]
            .name
        }</strong> à ce${
          Array.isArray(this.target) && this.target.length > 1
            ? 's'
            : this.target instanceof PatientGroup
            ? 's'
            : ''
        } patient${
          Array.isArray(this.target) && this.target.length > 1
            ? 's'
            : this.target instanceof PatientGroup
            ? 's'
            : ''
        } ? Si non, le profil actuel sera conservé.`,
        validateLabel: 'Oui',
        cancelLabel: 'Non',
      },
    };
    const confirm = this.modalService.show(
      ConfirmationModalComponent,
      initialState,
    );

    if (confirm.content instanceof ConfirmationModalComponent) {
      confirm.content.validate.subscribe(() => {
        if (this.dietitian) {
          this.updateTarget(
            this.target,
            this.selectedGroupId,
            this.dietitian,
            true,
          );
        }
      });
      confirm.content.cancel.subscribe(() => {
        if (this.dietitian) {
          this.updateTarget(
            this.target,
            this.selectedGroupId,
            this.dietitian,
            false,
          );
        }
      });
    }
  }

  private updateTarget(
    target: Patient[] | Patient | PatientGroup,
    selectedGroupId: string,
    dietitian: Dietitian,
    updateTrackers: boolean,
  ) {
    this.isAdding = true;
    if (Array.isArray(target)) {
      this.facade
        .addAllPatientsToGroup(
          dietitian.id.toString(),
          target.map((t) => t.id.toString()),
          selectedGroupId,
          updateTrackers,
        )
        .then((patients) => {
          this.validate.emit({
            groupId: this.selectedGroupId,
            target: patients,
          });
          this.hideModal();
        })
        .finally(() => {
          this.isAdding = false;
        });
    } else {
      if (target instanceof Patient) {
        this.facade
          .addPatientToGroup(
            dietitian.id.toString(),
            target.id.toString(),
            selectedGroupId,
            updateTrackers,
          )
          .then(() => {
            this.validate.emit({
              groupId: this.selectedGroupId,
              target: [this.target as Patient],
            });
            this.hideModal();
          })
          .finally(() => {
            this.isAdding = false;
          });
      } else {
        this.facade
          .addGroupToGroup(
            dietitian.id.toString(),
            target.id.toString(),
            selectedGroupId,
            updateTrackers,
          )
          .then((patients) => {
            this.validate.emit({
              groupId: this.selectedGroupId,
              target: patients,
            });
            this.hideModal();
          })
          .finally(() => {
            this.isAdding = false;
          });
      }
    }
  }

  targetName(): string {
    if (Array.isArray(this.target)) {
      if (this.target.length > 1) {
        return this.target.length + ' patients';
      } else {
        return this.target[0].fullName;
      }
    } else {
      if (this.target instanceof Patient) {
        return this.target.fullName;
      } else {
        return "tous les patients d'un groupe";
      }
    }
  }
}
